import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ItemModal } from "../components/ItemModal";
import { DashHero } from "../components/dashboard/DashHero";
import { ChampionsStrip } from "../components/dashboard/ChampionsStrip";
import { LifecycleSection } from "../components/dashboard/LifecycleSection";
import { LifecycleStory } from "../components/dashboard/LifecycleStory";
import { NewsFeed } from "../components/dashboard/NewsFeed";
import { PickedForYou, RecentlyAdded } from "../components/dashboard/SideRail";
import { useDashboardData } from "../hooks/useDashboardData";
import { useFeaturedSlides } from "../hooks/useFeaturedSlides";
import { useProfile } from "../hooks/useProfile";
import { useForYou } from "../hooks/useForYou";
import { useAINews, type NewsCategory } from "../hooks/useAINews";
import type { CatalogItem } from "../types/navigator";

/**
 * The redesigned dashboard (admins and AI Champions while in preview). Reads top to
 * bottom: welcome banner with the featured video → catalog numbers → how AI
 * fits homebuilding → the news → personal picks and recent updates.
 */
export function DashboardNewPage() {
  const state = useDashboardData();
  const { profile, photoUrl } = useProfile();
  const forYou = useForYou(profile);
  const news = useAINews();
  const featured = useFeaturedSlides();
  const [selected, setSelected] = useState<CatalogItem | null>(null);
  // ?life=tabs shows the earlier tabbed version, to compare the two.
  const [params] = useSearchParams();
  const [newsFilter, setNewsFilter] = useState<NewsCategory | "All">("All");

  return (
    <div className="page db">
      <DashHero
        firstName={profile?.givenName}
        slides={featured.slides}
        slidesLoading={featured.loading}
        counts={state.counts}
        loading={state.loadingCounts}
      />

      {state.errorCounts ? (
        <div className="error-banner" role="alert">
          <strong>Some numbers couldn't load.</strong> {state.errorCounts}
        </div>
      ) : null}

      <ChampionsStrip
        email={profile?.mail ?? profile?.userPrincipalName}
        department={profile?.department}
        name={profile?.displayName}
        title={profile?.jobTitle}
        photoUrl={photoUrl}
      />

      {params.get("life") === "tabs" ? (
        <LifecycleSection viewerTeam={profile?.department} onOpen={setSelected} />
      ) : (
        <LifecycleStory viewerTeam={profile?.department} onOpen={setSelected} />
      )}

      <NewsFeed {...news} filter={newsFilter} onFilter={setNewsFilter} />

      <PickedForYou
        items={forYou.personalized ? forYou.items : forYou.popularItems.length ? forYou.popularItems : forYou.items}
        loading={forYou.loading}
        personalized={forYou.personalized}
        matchedOn={forYou.matchedOn}
        onOpen={setSelected}
      />
      <RecentlyAdded items={state.recent} loading={state.loadingRecent} onOpen={setSelected} />

      {selected ? (
        <ItemModal
          item={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            state.refetch();
            forYou.refetch();
          }}
        />
      ) : null}
    </div>
  );
}
